import React from 'react';


import { useAuth } from '@components/utilComponents/AuthProvider';

import { createAppTheme } from '../ThemeManager.config';

import type { Theme } from '@mui/material/styles';

/**
 * Режим темы: настройка пользователя, затем `localStorage`, затем светлая.
 *
 * Порядок именно такой и одинаков у всех трёх приложений — эта логика и была
 * единственным, что у них совпадало, пока конфигурация лежала в компоненте
 * (AF-34).
 *
 * ─── Почему режим ВЫВОДИТСЯ, а не хранится (AF-45) ──────────────────────────
 *
 * Раньше здесь стояло `useState('light')` плюс эффект, который читал настройки
 * и звал `setThemeMode`. Линтер ругался на присваивание состояния синхронно в
 * эффекте, но лишний рендер — не главная беда.
 *
 * Главная в том, что `useState` тут был **не для состояния, а чтобы дождаться
 * данных**: единственным источником правды всегда оставались настройки
 * пользователя и `localStorage`, а состояние их лишь повторяло с задержкой в
 * один кадр. Отсюда и видимый дефект — первый кадр был светлым ВСЕГДА, и при
 * тёмной теме она мелькала.
 *
 * Замер до правки, вычисленный `theme.palette.mode` по кадрам:
 *
 *     настройка dark, storage пуст   кадры: light > dark
 *     только storage dark            кадры: light > dark
 *
 * Теперь режим считается прямо при рендере, и первый кадр сразу верный.
 * Эффект остался только для записи наружу — того, ради чего эффекты и нужны.
 *
 * `localStorage` читается один раз ленивым инициализатором, а не на каждом
 * рендере: значение не реактивно, перечитывать его незачем.
 *
 * `||`, а не `??`: разница только на пустой строке, и менять её заодно значило
 * бы протащить второе изменение поведения под видом первого.
 */
export const useThemeFromUser = (): Theme => {
  const user = useAuth();
  const [storedTheme] = React.useState(() => localStorage.getItem('theme'));

  const themeMode = (user?.settings?.theme || storedTheme || 'light') as
    | 'light'
    | 'dark';

  React.useEffect(() => {
    if (user?.settings?.theme) {
      localStorage.setItem('theme', user.settings.theme);
    }
  }, [user?.settings?.theme]);

  return React.useMemo(() => createAppTheme(themeMode), [themeMode]);
};
